import type { Color, Point, Sprite } from '../content/types';
import { drawSprite, spriteBounds, type Painter } from './painter';

/** bitmaps of sprites at a scale (src/draw/painter.ts `drawSpriteCached`): a WeakMap so a sprite's cache dies with it */
const bitmaps = new WeakMap<Sprite, Map<number, { img: HTMLCanvasElement | OffscreenCanvas; dx: number; dy: number }>>();
function makeCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas'); c.width = w; c.height = h; return c;
}

export class CanvasPainter implements Painter {
  constructor(public ctx: CanvasRenderingContext2D) {}

  begin(ox: number, oy: number, scale: number, rot = 0, cx = 0, cy = 0): void {
    const c = this.ctx;
    c.save();
    c.translate(ox, oy);
    c.scale(scale, scale);
    if (rot) { c.translate(cx, cy); c.rotate(rot); c.translate(-cx, -cy); }
    c.lineJoin = 'round';
    c.lineCap = 'round';
  }
  end(): void { this.ctx.restore(); }

  blit(sprite: Sprite, ox: number, oy: number, scale: number): boolean {
    let byScale = bitmaps.get(sprite); if (!byScale) { byScale = new Map(); bitmaps.set(sprite, byScale); }
    let e = byScale.get(scale);
    if (!e) {
      if (byScale.size >= 6) byScale.clear(); // (a zoom that never settles would otherwise pile up bitmaps)
      const b = spriteBounds(sprite);
      const x0 = Math.floor(Math.min(0, b.minX) * scale) - 1, y0 = Math.floor(Math.min(0, b.minY) * scale) - 1;
      const x1 = Math.ceil(Math.max(sprite.size[0], b.maxX) * scale) + 1, y1 = Math.ceil(Math.max(sprite.size[1], b.maxY) * scale) + 1;
      const w = x1 - x0, h = y1 - y0; if (w <= 0 || h <= 0 || w > 2048 || h > 2048) return false;
      const img = makeCanvas(w, h), ctx = img.getContext('2d') as CanvasRenderingContext2D | null; if (!ctx) return false;
      drawSprite(new CanvasPainter(ctx), sprite, -x0, -y0, scale);
      e = { img, dx: x0, dy: y0 }; byScale.set(scale, e);
    }
    this.ctx.drawImage(e.img as CanvasImageSource, ox + e.dx, oy + e.dy);
    return true;
  }

  private paint(fill?: Color, stroke?: Color, lw?: number): void {
    const c = this.ctx;
    if (fill) { c.fillStyle = fill; c.fill(); }
    if (stroke && (lw ?? 0) > 0) { c.strokeStyle = stroke; c.lineWidth = lw!; c.stroke(); }
  }
  rect(x: number, y: number, w: number, h: number, fill?: Color, stroke?: Color, lw?: number, rx?: number): void {
    const c = this.ctx;
    c.beginPath();
    if (rx) c.roundRect(x, y, w, h, rx); else c.rect(x, y, w, h);
    this.paint(fill, stroke, lw);
  }
  circle(x: number, y: number, r: number, fill?: Color, stroke?: Color, lw?: number): void {
    this.ctx.beginPath();
    this.ctx.arc(x, y, r, 0, Math.PI * 2);
    this.paint(fill, stroke, lw);
  }
  poly(pts: Point[], fill?: Color, stroke?: Color, lw?: number): void {
    const c = this.ctx;
    c.beginPath();
    c.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
    c.closePath();
    this.paint(fill, stroke, lw);
  }
  line(pts: Point[], stroke: Color, lw = 0.1): void {
    const c = this.ctx;
    c.beginPath();
    c.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
    c.strokeStyle = stroke; c.lineWidth = lw; c.stroke();
  }
}
